import { Injectable } from '@angular/core';
import { AlertController, ModalController, Platform } from '@ionic/angular';
import { AppService } from './app.service';
import { BehaviorSubject } from 'rxjs';
import { EncryptionService } from './encryption.service';
import { AppEnum } from '../appenum';
import { DeviceDetectorService } from 'ngx-device-detector';
import { LoggingService } from './logging.service';
import { environment } from 'src/environments/environment';
import { wrapAllMethodsWithLogging } from 'src/app/core/decorators/log-all-errors.decorator';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private permissionDeniedSubject = new BehaviorSubject<boolean>(false);
  public permissionDenied$ = this.permissionDeniedSubject.asObservable();

  private locationAllowedSubject = new BehaviorSubject<boolean>(false);
  locationAllowed$ = this.locationAllowedSubject.asObservable();
  watchId: number | null = null; // Holds the ID for geolocation watch
  locationCheckInterval: any = null; // Holds the interval for checking GPS status
  permissionStatusRef: PermissionStatus | null = null;
  isLocationPermissionEnable: any;
  componentName: string = 'permissionService';
  constructor(public alertController: AlertController,
    public _appservice: AppService,
    public encryptionService: EncryptionService,
    public _appenum: AppEnum,
    public deviceService: DeviceDetectorService,
    private modalCtrl: ModalController,
    public platformService: Platform,

    public loggingService: LoggingService,
  ) {
    wrapAllMethodsWithLogging(this, this.componentName);
    this.isLocationPermissionEnable = this.encryptionService.decrypt(
      this.encryptionService.localstorageDecrypt(
        this._appenum.EntityOfLocalStorageKeys.showLocationPermissionPage
      )
    );
  }
  // NEW OPTIMIZE CODE
  // ─────────────────────────────────────────────────────────────────────────────
  // permission.service.ts  –  Full fixed & enhanced version
  // ─────────────────────────────────────────────────────────────────────────────

  async checkGeolocationPermission(
    mode: 'required' | 'optional' | 'disabled' = 'required',
    isClick = false
  ): Promise<boolean> {

    const deviceInfo = this.deviceService.getDeviceInfo();
    const browserName = deviceInfo.browser?.toLowerCase() || '';
    const browserVer = parseInt(deviceInfo.browser_version?.split('.')[0], 10);

    // ── Shared helpers ────────────────────────────────────────────────────────

    const onGranted = (pos: GeolocationPosition): void => {
      this.storeLocation(pos);
      this.updatePermissionStatus(false);
      // this.updateLocationAllowed(true)
      this.closeModel();
      // Start watching only for 'required' mode
      if (mode === 'required') {
        this.startWatching(mode);
      }
      this.UpdateUserActivityOnPermission('Permission Granted By User');
    };

    const onDenied = (errMsg = ''): void => {
      if (mode === 'required') {
        const isOutdated =
          (browserName === 'chrome' && browserVer < 43) ||
          (browserName === 'safari' && browserVer < 16) ||
          (browserName === 'firefox' && browserVer < 46);

        if (isOutdated) {
          this._appservice.ionicCustomPromptForCors(
            'Warning',
            `Your ${deviceInfo.browser} (v${deviceInfo.browser_version}) is outdated.\nPlease update your browser.`
          );
        }
        if (errMsg) this.UpdateUserActivityOnPermission(errMsg);
        // this.updatePermissionStatus(true);
        // this._appservice.openModel(4);
      }
      // For optional: caller handles fallback — just return false cleanly
    };

    // ── Fetch actual coordinates ───────────────────────────────────────────────
    const fetchPosition = (): Promise<boolean> =>
      new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            onGranted(pos);
            resolve(true);
          },
          (err) => {
            onDenied(err.message);
            resolve(false);
          },
          { enableHighAccuracy: false, timeout: 20000, maximumAge: 10000 }
        );
      });

    // ── No Permissions API (old browsers / some WebViews) ────────────────────
    if (!navigator?.permissions) {
      try {
        return await fetchPosition();
      } catch (e) {
        this.logGeolocationError(
          'Exception in fallback geolocation', '', '', null, '', String(e)
        );
        onDenied();
        return false;
      }
    }

    // ── Modern Permissions API ────────────────────────────────────────────────
    try {
      const status = await navigator.permissions.query({ name: 'geolocation' });

      if (!status) {
        this.logGeolocationError('Failed to retrieve geolocation permission status');
        return false;
      }

      // ── Fix: listen for runtime permission changes (browser settings / lock icon)
      this.listenForPermissionChange(status, mode);

      switch (status.state) {
        case 'granted':
          // Always fetch coords even when already granted
          return await fetchPosition();

        case 'denied':
          onDenied('Permission already denied');
          return false;

        case 'prompt':
        default:
          // Triggers the browser dialog
          return await fetchPosition();
      }

    } catch (error) {
      console.error('Error checking geolocation permission:', error);
      this.logGeolocationError(
        'Error while checking geolocation permission', '', '', null, '', String(error)
      );
      onDenied();
      return false;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Fix: Real-time permission change listener
  // Handles: denied → allowed (from browser settings or lock icon)
  //          allowed → denied (user revokes mid-session)
  // ─────────────────────────────────────────────────────────────────────────────
  private listenForPermissionChange(
    status: PermissionStatus,
    mode: 'required' | 'optional' | 'disabled'
  ): void {

    // Remove previous listener to avoid duplicates
    if (this.permissionStatusRef) {
      this.permissionStatusRef.onchange = null;
    }
    this.permissionStatusRef = status;

    status.onchange = () => {
      console.log('[Permission] State changed to:', status.state, '| mode:', mode);

      if (status.state === 'granted') {
        // User allowed from settings/lock icon — fetch location & update UI
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            this.storeLocation(pos);
            this.updatePermissionStatus(false);
            // this.updateLocationAllowed(true);
            this.closeModel();
            this.UpdateUserActivityOnPermission('Permission Granted By User');

            // Update isLocationAllowed in localStorage
            // this.encryptionService.localstorageEncrypt(
            //   this._appenum.EntityOfLocalStorageKeys.isLocationAllowed, true
            // );

            // Restart watching for required mode
            if (mode === 'required') {
              this.startWatching(mode);
            }
          },
          (err) => {
            console.error('[Permission] Granted but failed to fetch position:', err);
            this.logGeolocationError(
              'Permission granted but position fetch failed', '', '', null, '', err.message
            );
          },
          { enableHighAccuracy: false, timeout: 20000, maximumAge: 10000 }
        );

      } else if (status.state === 'denied') {
        // User revoked from settings mid-session
        this.stopWatchingLocation();
        // this.updateLocationAllowed(false);
        this.UpdateUserActivityOnPermission('Permission Denied By User');

        // Update isLocationAllowed in localStorage
        // this.encryptionService.localstorageEncrypt(
        //   this._appenum.EntityOfLocalStorageKeys.isLocationAllowed, false
        // );

        if (mode === 'required') {
          // this.updatePermissionStatus(true);
          // this._appservice.openModel(4);
        } else if (mode === 'optional') {
          this.updatePermissionStatus(false);
          // Store fallback coords silently
          this.storeStaticLocation({ latitude: 91, longitude: 181, accuracy: 0 });
        }
      }
    };
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // startWatching: only for 'required' mode, replaces startCheckingLocation
  // ─────────────────────────────────────────────────────────────────────────────
  private startWatching(mode: 'required' | 'optional' | 'disabled'): void {
    if (mode !== 'required') return; // No watching needed for optional/disabled
    if (this.watchId !== null) return; // Already watching

    this.watchId = navigator.geolocation.watchPosition(
      (pos) => {
        this.storeLocation(pos);
        this.updatePermissionStatus(false);
        // this.updateLocationAllowed(true);
      },
      (error) => {
        this.logGeolocationError(
          'Error from watchPosition', 'GET', 'watchPosition', null, '', JSON.stringify(error)
        );
        // Do NOT recursively call checkGeolocationPermission here
        // The permissionchange listener handles state recovery
        this.handleLocationError(error, false);
      },
      { enableHighAccuracy: false, timeout: 20000, maximumAge: 10000 }
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // startCheckingLocation: interval-based fallback for browsers without
  // permissionchange support (some old Android WebViews / Samsung Browser)
  // Only runs for 'required' mode
  // ─────────────────────────────────────────────────────────────────────────────
  startCheckingLocation(mode: 'required' | 'optional' | 'disabled' = 'required'): void {
    if (mode !== 'required') return;
    if (this.locationCheckInterval) return;

    this.locationCheckInterval = setInterval(async () => {
      try {
        if (!navigator?.permissions) return;

        const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });

        if (permissionStatus.state === 'denied') {
          this.UpdateUserActivityOnPermission('Permission Denied By User');
          this.stopWatchingLocation();
          // Do NOT restart the interval recursively via restartLocationCheck
          // Just keep the interval running to detect when user re-enables
        } else if (permissionStatus.state === 'granted') {
          // Recover if watch was stopped
          if (this.watchId === null) {
            this.startWatching(mode);
          }
          this.updatePermissionStatus(false);
          // this.updateLocationAllowed(true);
          this.closeModel();
        } else if (permissionStatus.state === 'prompt') {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              this.storeLocation(pos);
              this.closeModel();
              this.updatePermissionStatus(false);
              // this.updateLocationAllowed(true);
            },
            (error) => this.handleLocationError(error, false),
            { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
          );
        }
      } catch (error) {
        console.error('Error in location check interval:', error);
      }
    }, 5000);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // storeLocation: only accepts GeolocationPosition (real coords)
  // ─────────────────────────────────────────────────────────────────────────────
  storeLocation(position: GeolocationPosition): void {
    console.log('storeLocation:', position);

    if (!position?.coords?.latitude || !position?.coords?.longitude) {
      // this.updatePermissionStatus(true);
      // this._appservice.openModel(4);
      this._appservice.presentToast(
        'Unable to fetch latitude/longitude. Please try later.',
        'custom-toast-error'
      );
      this.logGeolocationError(
        'GPS Granted but Lat/Long Not Found', '', '', null, '', '',
        position, ''
      );
      return;
    }

    this.encryptionService.localstorageEncrypt(
      this._appenum.EntityOfLocalStorageKeys.latitude, position.coords.latitude
    );
    this.encryptionService.localstorageEncrypt(
      this._appenum.EntityOfLocalStorageKeys.longitude, position.coords.longitude
    );
    this.encryptionService.localstorageEncrypt(
      this._appenum.EntityOfLocalStorageKeys.accuracy, position.coords.accuracy
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // storeStaticLocation: for optional-deny and disabled modes (91, 181)
  // Uses a plain object — NOT GeolocationPosition, so no coords wrapper needed
  // ─────────────────────────────────────────────────────────────────────────────
  storeStaticLocation(position: { latitude: number; longitude: number; accuracy: number }): void {
    this.encryptionService.localstorageEncrypt(
      this._appenum.EntityOfLocalStorageKeys.latitude, position.latitude
    );
    this.encryptionService.localstorageEncrypt(
      this._appenum.EntityOfLocalStorageKeys.longitude, position.longitude
    );
    this.encryptionService.localstorageEncrypt(
      this._appenum.EntityOfLocalStorageKeys.accuracy, position.accuracy
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // getUserLocation: called only for 'required' mode explicit re-requests
  // ─────────────────────────────────────────────────────────────────────────────
  async getUserLocation(isClick = false, mode: 'required' | 'optional' | 'disabled' = 'required'): Promise<void> {
    if (!navigator.geolocation) {
      this.logGeolocationError('Geolocation API not supported', 'GET', 'getUserLocation');
      // this.updatePermissionStatus(true);
      return;
    }

    try {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.storeLocation(position);
          this.closeModel();
          this.updatePermissionStatus(false);
          // this.updateLocationAllowed(true);
          // Start watching after successful get
          this.startWatching(mode);
        },
        (error) => {
          console.error('Error getting current position:', error);
          this.UpdateUserActivityOnPermission(error.message);
          this.logGeolocationError(
            'Error getting current position', 'GET', 'getUserLocation',
            null, '', JSON.stringify(error)
          );
          // this._appservice.openModel(4);
          // this.updatePermissionStatus(true);
          this.handleLocationError(error, isClick);
        },
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
      );
    } catch (err) {
      console.error('Unexpected exception in getUserLocation:', err);
      this.logGeolocationError(
        'Unexpected exception in getUserLocation', 'GET', 'getUserLocation',
        null, '', String(err)
      );
      // this.updatePermissionStatus(true);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // handlePermissionState: Fix — no longer calls requestUserLocation() or
  // restartLocationCheck() to avoid double-prompt conflict
  // ─────────────────────────────────────────────────────────────────────────────
  handlePermissionState(
    state: PermissionState,
    isClick: boolean,
    mode: 'required' | 'optional' | 'disabled' = 'required'
  ): void {
    switch (state) {
      case 'granted':
        this.UpdateUserActivityOnPermission('Permission Granted By User');
        // getUserLocation is called separately — don't duplicate here
        break;

      case 'prompt':
        // Just log; checkGeolocationPermission's fetchPosition handles the dialog
        this.logGeolocationError(
          'Geolocation permission in prompt state',
          'GET', 'handlePermissionState', null, 'prompt', ''
        );
        break;

      default: // 'denied'
        this.UpdateUserActivityOnPermission('Permission Denied By User');
        this.logGeolocationError(
          'Permission denied or unknown state',
          'GET', 'handlePermissionState', null, state
        );

        if (mode === 'required') {
          // this.updatePermissionStatus(true);
          // this._appservice.openModel(4);
        }
        break;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // stopWatchingLocation
  // ─────────────────────────────────────────────────────────────────────────────
  stopWatchingLocation(): void {
    // this.updatePermissionStatus(true);

    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    if (this.locationCheckInterval !== null) {
      clearInterval(this.locationCheckInterval);
      this.locationCheckInterval = null;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // restartLocationCheck — only used as explicit recovery, not recursively
  // ─────────────────────────────────────────────────────────────────────────────
  restartLocationCheck(mode: 'required' | 'optional' | 'disabled' = 'required'): void {
    if (mode !== 'required') return;
    this.stopWatchingLocation();
    setTimeout(() => this.startCheckingLocation(mode), 3000);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // requestUserLocation — kept for explicit manual re-request (e.g. retry button)
  // ─────────────────────────────────────────────────────────────────────────────
  requestUserLocation(): void {
    // this.updatePermissionStatus(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.storeLocation(position);
        this.updatePermissionStatus(false);
      },
      (error) => {
        // this.updatePermissionStatus(true);
        this.UpdateUserActivityOnPermission(error.message);
        // this._appservice.openModel(4);
        console.error('Location error:', error);
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // handleLocationError — unchanged, kept for reference
  // ─────────────────────────────────────────────────────────────────────────────
  handleLocationError(error: any, isClick: boolean): void {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        this.logGeolocationError(
          'Permission denied by user', 'GET',
          'navigator.geolocation.getCurrentPosition',
          null, String(error.code), error.message
        );
        // this.updatePermissionStatus(true);
        break;
      case error.POSITION_UNAVAILABLE:
        this.logGeolocationError(
          'GPS is disabled or unavailable', 'GET',
          'navigator.geolocation.getCurrentPosition',
          null, String(error.code), error.message
        );
        // this.updatePermissionStatus(true);
        break;
      case error.TIMEOUT:
        this.logGeolocationError(
          'Location request timed out', 'GET',
          'navigator.geolocation.getCurrentPosition',
          null, String(error.code), error.message
        );
        // this.updatePermissionStatus(true);
        if (isClick)
          // this._appservice.openModel(3);
          break;
      default:
        this.logGeolocationError(
          'Unknown location error', 'GET',
          'navigator.geolocation.getCurrentPosition',
          null, String(error.code), error.message
        );
        // this.updatePermissionStatus(true);
        break;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Caller block (in your component/guard) — normalized mode detection
  // ─────────────────────────────────────────────────────────────────────────────
  /*
    const raw = this.encryptionService.decrypt(
      this.encryptionService.localstorageDecrypt(
        this._appenum.EntityOfLocalStorageKeys.showLocationPermissionPage
      )
    );
  
    // Normalize to typed mode — avoids string vs boolean issues
    type LocationMode = 'required' | 'optional' | 'disabled';
    const mode: LocationMode =
      (raw === true || raw === 'true') ? 'required'
      : raw === 'optional'             ? 'optional'
      :                                  'disabled';
  
    const staticPosition = { latitude: 91, longitude: 181, accuracy: 0 };
  
    if (mode === 'required') {
      const canNavigate = await this.permissionService.checkGeolocationPermission('required');
      if (canNavigate) {
        this.encryptionService.localstorageEncrypt(
          this._appenum.EntityOfLocalStorageKeys.isLocationAllowed, true
        );
        this.goToConfirmAddress();
      }
      // else: modal already open, user must grant before proceeding
  
    } else if (mode === 'optional') {
      const canNavigate = await this.permissionService.checkGeolocationPermission('optional');
      this.encryptionService.localstorageEncrypt(
        this._appenum.EntityOfLocalStorageKeys.isLocationAllowed, canNavigate
      );
      if (!canNavigate) {
        this.permissionService.storeStaticLocation(staticPosition);
      }
      this.goToConfirmAddress(); // always navigate in optional
  
    } else {
      // disabled — skip entirely
      this.permissionService.storeStaticLocation(staticPosition);
      this.encryptionService.localstorageEncrypt(
        this._appenum.EntityOfLocalStorageKeys.isLocationAllowed, false
      );
      this.goToConfirmAddress();
    }
  */

  // ─────────────────────────────────────────────────────────────────────────────
  // Unchanged utility methods (kept as-is)
  // ─────────────────────────────────────────────────────────────────────────────

  logGeolocationError(
    message: string,
    method: string = '',
    url: string = '',
    payload: any = null,
    responseCode: string = '',
    responseMsg: string = '',
    result: any = '',
    taskDetail: any = ''
  ) {
    const paramRequest = {
      httpRequest: {
        method,
        url,
        payLoad: payload,
        response: { responseCode, responseMsg, result }
      },
      taskDetails: {
        TaskId: this.encryptionService.decrypt(
          this.encryptionService.localstorageDecrypt(
            this._appenum.EntityOfLocalStorageKeys.taskId
          )
        )
      }
    };

    this._appservice.logsActivity(
      this._appenum.EntityOfLogLevel.Info,
      message,
      'Permission-service',
      paramRequest.httpRequest,
      paramRequest.taskDetails
    );
  }

  updatePermissionStatus(status: boolean): void {
    this.permissionDeniedSubject.next(status);
  }

  async closeModel(): Promise<void> {
    const topModal = await this.modalCtrl.getTop();
    if (topModal) {
      this.modalCtrl.dismiss({}, '', 'permissionModel');
    }
  }

  async UpdateUserActivityOnPermission(message: string | null = null): Promise<void> {
    const postJson = {
      taskId: this.encryptionService.decrypt(
        this.encryptionService.localstorageDecrypt(
          this._appenum.EntityOfLocalStorageKeys.taskId
        )
      ),
      userType: 'User',
      userId: this.encryptionService.decrypt(
        this.encryptionService.localstorageDecrypt(
          this._appenum.EntityOfLocalStorageKeys.userid
        )
      ),
      taskMode: 'INSP2',
      activity: message
    };

    const logRequest: any = {
      apiEndUrl: 'createTaskUserActivity',
      reqBody: postJson,
    };
    (await this._appservice.postData(environment.endpoint.createTaskUserActivity, postJson)).subscribe(
      _res => {
        console.log("_res", _res)
        this.loggingService.handleResponseLog(
          _res.StatusCode == 200 ? 'info' : 'error',
          logRequest,
          _res.StatusCode,
          _res.Message,
          'createTaskUserActivity Api Call',
          this.componentName,
          environment.endpoint.createTaskUserActivity
        );
      },
      err => {
        this.loggingService.logApiError(
          'error',
          logRequest,
          err,
          'createTaskUserActivity Api Call',
          this.componentName,
          environment.endpoint.createTaskUserActivity
        )
      }
    );
  }

  // updateLocationAllowed(allowed: boolean): void {
  //   this.encryptionService.localstorageEncrypt(
  //     this._appenum.EntityOfLocalStorageKeys.isLocationAllowed, allowed
  //   );
  //   this.locationAllowedSubject.next(allowed);
  // }
} 